import {Button, Flag} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {type ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  ServiceIntervalPlan,
  ServiceIntervalPlanPartsCallout,
  ServiceIntervalPlanStep,
  ServiceIntervalPlanStepBucket,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useTenant} from '../../../hooks/useTenant';

type FormatNumber = ReturnType<typeof useFormatNumber>;
type FormatPrice = (price: number | Nullish) => string;
type BucketFlag = {label: string; colorScheme: 'red' | 'orange' | 'neutral'};

interface VehicleServiceIntervalPlanProps extends RequiredTestIdProps {
  plan: ServiceIntervalPlan | Nullish;
  onApply?: () => void;
  isApplyLoading?: boolean;
}

export function VehicleServiceIntervalPlan(props: VehicleServiceIntervalPlanProps) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const {tenantCurrency} = useTenant();

  if (isNil(props.plan)) {
    return null;
  }

  const plan = props.plan;

  const formatPrice: FormatPrice = (price) =>
    isNotNil(price)
      ? (formatCurrency(price, tenantCurrency, 2) ?? EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;

  const dueNowSteps = plan.steps.filter((step) => step.bucket === 'dueNow');
  const upcomingSteps = plan.steps.filter((step) => step.bucket === 'upcoming');
  const excludedSteps = plan.steps.filter((step) => step.bucket === 'excluded');

  return (
    <Box
      border="1px solid"
      borderColor="palettes.blue.20.100"
      borderRadius="small"
      backgroundColor="palettes.blue.10.100"
      padding={4}
      data-testid={suffixTestId('plan', props)}
    >
      <VStack spacing={4}>
        <HStack spacing={3} justify="space-between" align="flex-start" wrap>
          <VStack spacing={1}>
            <Heading size={4}>{i18n.t('entity.vehicle.serviceIntervalPlan.title')}</Heading>
            <Text size="xSmall" color="secondary">
              {formatPlanSubtitle(plan, formatNumber)}
            </Text>
          </VStack>
          <Flag
            label={i18n.t('entity.vehicle.serviceIntervalPlan.selectedItems', {
              count: plan.selectedItemsCount,
            })}
            colorScheme="green"
            size="small"
            isSubtle
          />
        </HStack>

        <Grid columns={[1, 2, 4, 4]} spacing={3}>
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.dueNowMetric')}
            value={String(plan.metrics.dueNowCount)}
          />
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.nextServiceMetric')}
            value={formatMileage(plan.metrics.nextServiceMileage, formatNumber)}
          />
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.estimatedWorkMetric')}
            value={formatHours(plan.metrics.estimatedWorkHoursNow)}
          />
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.excludedMetric')}
            value={String(plan.metrics.excludedCount)}
          />
        </Grid>

        <PlanPartsCallout
          partsCallout={plan.partsCallout}
          formatPrice={formatPrice}
          data-testid={suffixTestId('partsCallout', props)}
        />

        <Show when={isNotNilOrEmpty(dueNowSteps)}>
          <PlanStepGroup
            title={i18n.t('entity.vehicle.serviceIntervalPlan.dueNowTitle')}
            termColumnLabel={i18n.t('entity.vehicle.serviceIntervalPlan.columnTerm')}
            steps={dueNowSteps}
            formatNumber={formatNumber}
            formatPrice={formatPrice}
            data-testid={suffixTestId('dueNow', props)}
          />
        </Show>

        <Show when={isNotNilOrEmpty(upcomingSteps)}>
          <PlanStepGroup
            title={i18n.t('entity.vehicle.serviceIntervalPlan.upcomingTitle')}
            termColumnLabel={i18n.t('entity.invoice.labels.remaining')}
            steps={upcomingSteps}
            formatNumber={formatNumber}
            formatPrice={formatPrice}
            data-testid={suffixTestId('upcoming', props)}
          />
        </Show>

        <Show when={isNotNilOrEmpty(excludedSteps)}>
          <PlanStepGroup
            title={i18n.t('entity.vehicle.serviceIntervalPlan.excludedTitle')}
            termColumnLabel={i18n.t('general.labels.reason')}
            steps={excludedSteps}
            formatNumber={formatNumber}
            formatPrice={formatPrice}
            data-testid={suffixTestId('excluded', props)}
          />
        </Show>

        <Show when={isNotNil(props.onApply)}>
          <HStack justify="flex-end">
            <Button
              variant="primary"
              size="small"
              leftIcon="action/done"
              title={i18n.t('entity.vehicle.actions.applyAiServiceIntervals')}
              onClick={props.onApply}
              isLoading={props.isApplyLoading}
              isDisabled={props.isApplyLoading}
              data-testid={suffixTestId('apply', props)}
            />
          </HStack>
        </Show>
      </VStack>
    </Box>
  );
}

interface PlanMetricTileProps {
  label: string;
  value: string;
}

function PlanMetricTile(props: PlanMetricTileProps) {
  return (
    <Box
      borderRadius="small"
      backgroundColor="palettes.white.10.100"
      border="1px solid"
      borderColor="palettes.neutral.30.100"
      padding={3}
      minHeight={16}
    >
      <VStack spacing={1}>
        <Text size="xxSmall" color="secondary">
          {props.label}
        </Text>
        <Text size="large" alternative>
          {props.value}
        </Text>
      </VStack>
    </Box>
  );
}

interface PlanPartsCalloutProps extends RequiredTestIdProps {
  partsCallout: ServiceIntervalPlanPartsCallout | Nullish;
  formatPrice: FormatPrice;
}

function PlanPartsCallout(props: PlanPartsCalloutProps) {
  if (isNil(props.partsCallout)) {
    return null;
  }

  const partsCallout = props.partsCallout;

  return (
    <Box
      border="1px solid"
      borderColor="palettes.yellow.40.100"
      borderRadius="small"
      backgroundColor="palettes.yellow.10.100"
      padding={3}
      data-testid={props['data-testid']}
    >
      <VStack spacing={2}>
        <Heading size={5}>{i18n.t('entity.vehicle.serviceIntervalPlan.partsCalloutTitle')}</Heading>
        <Grid columns={[1, 3, 3, 3]} spacing={3}>
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.priceRange')}
            value={formatPriceRange(
              partsCallout.priceRangeFrom,
              partsCallout.priceRangeTo,
              props.formatPrice
            )}
          />
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.compatibleVariants')}
            value={
              isNotNil(partsCallout.compatibleVariantsCount)
                ? String(partsCallout.compatibleVariantsCount)
                : EMPTY_PLACEHOLDER
            }
          />
          <PlanMetricTile
            label={i18n.t('entity.vehicle.serviceIntervalPlan.requiredPrCodes')}
            value={
              isNotNilOrEmpty(partsCallout.requiredPrCodes)
                ? partsCallout.requiredPrCodes.join(' / ')
                : EMPTY_PLACEHOLDER
            }
          />
        </Grid>
      </VStack>
    </Box>
  );
}

interface PlanStepGroupProps extends RequiredTestIdProps {
  title: string;
  termColumnLabel: string;
  steps: ServiceIntervalPlanStep[];
  formatNumber: FormatNumber;
  formatPrice: FormatPrice;
}

function PlanStepGroup(props: PlanStepGroupProps) {
  return (
    <VStack spacing={2} data-testid={props['data-testid']}>
      <Text size="small" alternative>
        {props.title}
      </Text>
      <Box
        border="1px solid"
        borderColor="palettes.neutral.30.100"
        borderRadius="small"
        backgroundColor="palettes.white.10.100"
        overflowX="auto"
      >
        <VStack>
          <Grid columns={[1, 1, 4, 4]} spacing={0}>
            <PlanHeaderCell label={i18n.t('entity.vehicle.serviceIntervalPlan.columnWork')} />
            <PlanHeaderCell label={i18n.t('entity.vehicle.serviceIntervalPlan.columnInterval')} />
            <PlanHeaderCell label={props.termColumnLabel} />
            <PlanHeaderCell label={i18n.t('general.labels.time')} />
          </Grid>
          {props.steps.map((step, index) => (
            <PlanStepRow
              key={`${step.workId ?? step.workPos ?? step.workName}-${step.intervalMileage ?? ''}`}
              step={step}
              formatNumber={props.formatNumber}
              formatPrice={props.formatPrice}
              data-testid={suffixTestId(`step.${index}`, props)}
            />
          ))}
        </VStack>
      </Box>
    </VStack>
  );
}

interface PlanStepRowProps extends RequiredTestIdProps {
  step: ServiceIntervalPlanStep;
  formatNumber: FormatNumber;
  formatPrice: FormatPrice;
}

function PlanStepRow(props: PlanStepRowProps) {
  const termFlag = getBucketFlag(props.step, props.formatNumber);

  return (
    <VStack data-testid={props['data-testid']}>
      <Grid columns={[1, 1, 4, 4]} spacing={0}>
        <PlanCell>
          <VStack spacing={1}>
            <Text size="xSmall" alternative>
              {props.step.workName}
            </Text>
            <Show when={isNotNilOrEmpty(props.step.criteria)}>
              <Text size="xxSmall" color="tertiary">
                {props.step.criteria}
              </Text>
            </Show>
          </VStack>
        </PlanCell>
        <PlanCell>
          <Text size="xSmall">
            {formatInterval(
              props.step.intervalMileage,
              props.step.intervalMonths,
              props.formatNumber
            )}
          </Text>
        </PlanCell>
        <PlanCell>
          <Flag label={termFlag.label} colorScheme={termFlag.colorScheme} size="small" isSubtle />
        </PlanCell>
        <PlanCell>
          <Text size="xSmall">{formatHours(props.step.normTimeHours)}</Text>
        </PlanCell>
      </Grid>
      <Show when={isNotNilOrEmpty(props.step.parts)}>
        <PlanPartsTable
          step={props.step}
          formatPrice={props.formatPrice}
          data-testid={suffixTestId('parts', props)}
        />
      </Show>
    </VStack>
  );
}

interface PlanPartsTableProps extends RequiredTestIdProps {
  step: ServiceIntervalPlanStep;
  formatPrice: FormatPrice;
}

function PlanPartsTable(props: PlanPartsTableProps) {
  return (
    <Box
      borderTop="1px solid"
      borderColor="palettes.neutral.30.100"
      backgroundColor="palettes.neutral.10.100"
      padding={3}
      data-testid={props['data-testid']}
    >
      <VStack spacing={2}>
        <HStack spacing={2} justify="space-between" align="center" wrap>
          <Text size="xSmall" alternative>
            {i18n.t('entity.vehicle.serviceIntervalPlan.partsAndMaterial')}
          </Text>
          <Show when={props.step.requiresClarification}>
            <Flag
              label={i18n.t('entity.vehicle.serviceIntervalPlan.requiresClarification')}
              colorScheme="orange"
              size="small"
              isSubtle
            />
          </Show>
        </HStack>
        <Box
          border="1px solid"
          borderColor="palettes.neutral.30.100"
          borderRadius="small"
          backgroundColor="palettes.white.10.100"
          overflowX="auto"
        >
          <VStack>
            <Grid columns={[1, 1, 4, 4]} spacing={0}>
              <PlanHeaderCell label={i18n.t('entity.claimType.labels.partNumber')} />
              <PlanHeaderCell label={i18n.t('general.labels.price')} />
              <PlanHeaderCell
                label={i18n.t('entity.vehicle.serviceIntervalPlan.compatibilityCriteria')}
              />
              <PlanHeaderCell label={i18n.t('general.labels.type')} />
            </Grid>
            {props.step.parts.map((part) => (
              <Grid
                key={`${part.partNumber}-${part.compatibilityCriteria ?? ''}`}
                columns={[1, 1, 4, 4]}
                spacing={0}
              >
                <PlanCell>
                  <Text size="xSmall" alternative>
                    {part.partNumber}
                  </Text>
                </PlanCell>
                <PlanCell>
                  <Text size="xSmall" noWrap>
                    {props.formatPrice(part.price)}
                  </Text>
                </PlanCell>
                <PlanCell>
                  <Text size="xSmall">{part.compatibilityCriteria ?? EMPTY_PLACEHOLDER}</Text>
                </PlanCell>
                <PlanCell>
                  <Text size="xSmall">{part.type ?? EMPTY_PLACEHOLDER}</Text>
                </PlanCell>
              </Grid>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}

function PlanHeaderCell(props: {label: string}) {
  return (
    <Box
      padding={2}
      borderBottom="1px solid"
      borderColor="palettes.neutral.30.100"
      backgroundColor="palettes.neutral.10.100"
    >
      <Text size="xxSmall" color="secondary">
        {props.label}
      </Text>
    </Box>
  );
}

function PlanCell(props: {children: ReactNode}) {
  return (
    <Box padding={2} borderTop="1px solid" borderColor="palettes.neutral.30.100">
      {props.children}
    </Box>
  );
}

function getBucketFlag(step: ServiceIntervalPlanStep, formatNumber: FormatNumber): BucketFlag {
  return match<ServiceIntervalPlanStepBucket, BucketFlag>(step.bucket)
    .with('dueNow', () => ({
      label: i18n.t('entity.vehicle.serviceIntervalPlan.dueNowPill'),
      colorScheme: 'red',
    }))
    .with('upcoming', () => ({
      label: isNotNil(step.remainingMileage)
        ? `+${formatNumber(step.remainingMileage, 0)} ${i18n.t('general.metric.km')}`
        : i18n.t('general.labels.planned'),
      colorScheme: 'orange',
    }))
    .with('excluded', () => ({
      label: step.exclusionReason ?? i18n.t('entity.vehicle.serviceIntervalPlan.excludedMetric'),
      colorScheme: 'neutral',
    }))
    .exhaustive();
}

function formatPlanSubtitle(plan: ServiceIntervalPlan, formatNumber: FormatNumber) {
  return [
    plan.intervalType,
    i18n.t('entity.vehicle.serviceIntervalPlan.calculatedFor', {
      mileage: `${formatNumber(plan.calculatedForMileage, 0)} ${i18n.t('general.metric.km')}`,
    }),
  ]
    .filter(isNotNilOrEmpty)
    .join(' · ');
}

function formatMileage(mileage: number | Nullish, formatNumber: FormatNumber) {
  return isNotNil(mileage)
    ? `${formatNumber(mileage, 0)} ${i18n.t('general.metric.km')}`
    : EMPTY_PLACEHOLDER;
}

function formatInterval(
  intervalMileage: number | Nullish,
  intervalMonths: number | Nullish,
  formatNumber: FormatNumber
) {
  const parts = [
    isNotNil(intervalMileage)
      ? `${formatNumber(intervalMileage, 0)} ${i18n.t('general.metric.km')}`
      : null,
    isNotNil(intervalMonths)
      ? `${formatNumber(intervalMonths, 0)} ${i18n.t('general.metric.months')}`
      : null,
  ].filter(isNotNil);

  return isNotNilOrEmpty(parts) ? parts.join(' / ') : EMPTY_PLACEHOLDER;
}

function formatHours(hours: number | Nullish) {
  return isNotNil(hours) ? `${hours} ${i18n.t('general.metric.h')}` : EMPTY_PLACEHOLDER;
}

function formatPriceRange(from: number | Nullish, to: number | Nullish, formatPrice: FormatPrice) {
  if (isNil(from) && isNil(to)) {
    return EMPTY_PLACEHOLDER;
  }

  return [from, to]
    .filter(isNotNil)
    .map((value) => formatPrice(value))
    .join(' – ');
}
